/**
 * Crystal ZIP download — bundles extxyz, CIF, analysis JSON, and report PDFs.
 * Extracted from AppShell.jsx so tests can import it directly.
 */

import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { htmlToPdf } from './htmlToPdf';
import {
  buildScientificDocHTML,
  buildEconomicReportHTML,
  buildPatentPrintHTML,
} from './builders';

export async function downloadCrystalAll(frame, cifUrl, trajUrl, cachedAnalyses = {}, cifText = null, fetchFn = fetch) {
  const n = frame.index + 1;
  const base = `crystal_${n}`;
  const zip = new JSZip();
  const fetches = [];

  // extxyz block
  zip.file(`${base}.extxyz`, `${frame.n}\n${frame.comment}\n${(frame.atomLines || []).join('\n')}\n`);

  // .cif file — use cached text if available, otherwise fetch URL
  if (cifText) {
    zip.file(`${base}.cif`, cifText);
  } else if (cifUrl) {
    fetches.push(
      fetchFn(cifUrl).then(r => r.ok ? r.blob() : null)
        .then(b => b && zip.file(`${base}.cif`, b))
        .catch(() => {})
    );
  }

  // per-crystal trajectory file
  if (trajUrl) {
    const trajExt = String(trajUrl).split('.').pop() || 'extxyz';
    fetches.push(
      fetchFn(trajUrl).then(r => r.ok ? r.blob() : null)
        .then(b => b && zip.file(`${base}_trajectory.${trajExt}`, b))
        .catch(() => {})
    );
  }

  // cached structural analyses as JSON
  const jsonKinds = {
    symmetry: 'symmetry', voronoi: 'voronoi', bond_distribution: 'bond_distribution',
    dft_surrogate: 'dft_surrogate', dft_singlepoint: 'dft_singlepoint',
    dft_relax: 'dft_relax', dft_elastic: 'dft_elastic', dft_elastic_tensor: 'dft_elastic_tensor',
    phase_stability: 'phase_stability', mp_neighbors: 'mp_neighbors',
    dft_dos: 'dft_dos', dft_bandstructure: 'dft_bandstructure',
    dft_spin_density: 'dft_spin_density',
    dft_optical: 'dft_optical',
    dft_effective_mass: 'dft_effective_mass',
    qe_singlepoint: 'qe_singlepoint', qe_relax: 'qe_relax', qe_elastic: 'qe_elastic',
    qe_dos: 'qe_dos', qe_bandstructure: 'qe_bandstructure',
    qe_optical: 'qe_optical', qe_effective_mass: 'qe_effective_mass',
    qe_phonon: 'qe_phonon', qe_thermal: 'qe_thermal',
  };
  for (const [kind, label] of Object.entries(jsonKinds)) {
    const d = cachedAnalyses[kind];
    if (d) {
      zip.file(`${base}_${label}.json`, JSON.stringify(d, null, 2));
      if ((kind === 'dft_relax' || kind === 'qe_relax') && d.relaxed_cif) {
        zip.file(`${base}_${kind === 'qe_relax' ? 'qe_' : ''}relaxed.cif`, d.relaxed_cif);
      }
      if (kind === 'dft_spin_density' && d.spin_density_cube) {
        zip.file(`${base}_spin_density.cube`, d.spin_density_cube);
      }
    }
  }

  // Helper: convert HTML to PDF, fall back to HTML on failure
  async function addReport(filename, html) {
    try {
      const pdfBuf = await htmlToPdf(html, fetchFn);
      zip.file(`${base}_${filename}.pdf`, pdfBuf);
    } catch (_) {
      zip.file(`${base}_${filename}.html`, html);
    }
  }

  // Reports — convert to PDF via the html-to-pdf endpoint
  const reportPromises = [];

  const sciDoc = cachedAnalyses['scientific_doc'];
  if (sciDoc?.ok) {
    reportPromises.push(addReport('scientific_document', buildScientificDocHTML(sciDoc)));
  }

  const econReport = cachedAnalyses['economic_report'];
  if (econReport?.ok || econReport?.economic_metrics) {
    reportPromises.push(addReport('economic_report', buildEconomicReportHTML(econReport)));
  }

  const patent = cachedAnalyses['patent_packet'];
  if (patent?.ok) {
    const ap = patent.appendix || {};
    const html = buildPatentPrintHTML(patent, ap.structural || {}, ap.symmetry, ap.bonding, ap.coordination, ap.economic, null);
    reportPromises.push(addReport('patent_packet', html));
  }

  await Promise.all([...fetches, ...reportPromises]);
  const isNode = typeof process !== 'undefined' && typeof process.versions?.node === 'string';
  const blob = await zip.generateAsync({ type: isNode ? 'nodebuffer' : 'blob' });
  saveAs(blob, `${base}_${frame.formula || 'unknown'}.zip`);
}
